import React from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import { Alert } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { useGetAccount } from '~/hooks/apis/offers/useGetAccount';

import { formatSyncedDate } from '../../Assets/utils';
import AccountListPageHeader from '../AccountListPageHeader';
import { useAccountRelatedFullList } from '../useAccountRelatedRows';

import { toAccountCase } from './toAccountCase';
import type { AccountCase } from './types';

const sharedOpts: Partial<GridColDef<AccountCase>> = {
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
};

const columns: Array<GridColDef<AccountCase>> = [
  { field: 'caseNumber', headerName: 'Case Number', flex: 1, minWidth: 130, ...sharedOpts },
  {
    field: 'contactName',
    headerName: 'Contact Name',
    flex: 1,
    minWidth: 160,
    renderCell: ({ row }) => row.contactName || '—',
    ...sharedOpts,
  },
  {
    field: 'subject',
    headerName: 'Subject',
    flex: 3,
    minWidth: 280,
    renderCell: ({ row }) => row.subject || '—',
    ...sharedOpts,
  },
  {
    field: 'priority',
    headerName: 'Priority',
    flex: 1,
    minWidth: 100,
    renderCell: ({ row }) => row.priority || '—',
    ...sharedOpts,
  },
  {
    field: 'createdDate',
    headerName: 'Date Opened',
    flex: 1,
    minWidth: 180,
    renderCell: ({ row }) => formatSyncedDate(row.createdDate) ?? '—',
    ...sharedOpts,
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 1,
    minWidth: 200,
    renderCell: ({ row }) => row.status || '—',
    ...sharedOpts,
  },
];

export default function AccountCasesListPage() {
  const { id_account: accountId } = useParams<{ id_account: string }>();
  const { data: account } = useGetAccount(accountId);
  const {
    count,
    error,
    isLoading,
    isTruncated,
    rows: cases,
  } = useAccountRelatedFullList(accountId, 'cases', toAccountCase);

  return (
    <>
      <Helmet>
        <title>Cases | {account?.name || accountId}</title>
      </Helmet>

      <AccountListPageHeader
        accountId={accountId}
        count={count}
        isTruncated={isTruncated}
        sortedBy="Date Opened"
        title="Cases"
      />

      {error ? (
        <Alert severity="error">Could not load cases: {error.message}</Alert>
      ) : (
        <DataGrid
          autoHeight
          disableRowSelectionOnClick
          columns={columns}
          loading={isLoading}
          rows={cases}
          initialState={{
            pagination: { paginationModel: { pageSize: 100 } },
            sorting: { sortModel: [{ field: 'createdDate', sort: 'desc' }] },
          }}
          pageSizeOptions={[25, 50, 100]}
        />
      )}
    </>
  );
}
